import { useState, useCallback } from 'react';
import { useAdminAuth } from '../lib/auth-context.js';
import { Badge, type BadgeVariant } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import { EmptyState } from '@heyroot/ui/primitives/EmptyState';
import { ErrorState } from '@heyroot/ui/primitives/ErrorState';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderCell,
} from '@heyroot/ui/primitives/Table';
import { Tabs, Tab } from '@heyroot/ui/primitives/Tabs';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchOnboardingQueue,
  fetchOnboardingQueueStats,
  claimOnboardingTenant,
  queryKeys,
  type OnboardingQueueItem,
} from '../lib/api.js';

function formatClaimExpiry(expiresAt: string | null): string {
  if (!expiresAt) return '';
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return 'Expired';
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function claimExpiryClass(expiresAt: string | null): string {
  if (!expiresAt) return '';
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return 'text-danger-text';
  if (diff < 3_600_000) return 'text-warning-text';
  return 'text-muted';
}

function Chip({
  label,
  value,
  loading,
}: {
  label: string;
  value: number | null;
  loading: boolean;
}) {
  return (
    <div className="flex flex-col items-center rounded-control border border-border bg-surface-elevated px-4 py-2 min-w-[80px]">
      <span className="text-heading-2 tabular-nums text-primary">
        {loading ? '—' : (value ?? '—')}
      </span>
      <span className="text-caption text-muted">{label}</span>
    </div>
  );
}

type StatusFilter =
  | 'all'
  | 'unclaimed'
  | 'claimed_by_me'
  | 'claimed_by_other'
  | 'expired'
  | 'snoozed'
  | 'new_applications';

function claimVariant(claimStatus: string): BadgeVariant {
  switch (claimStatus) {
    case 'claimed_by_me':
      return 'success';
    case 'claimed_by_other':
      return 'warning';
    default:
      return 'neutral';
  }
}

export function OnboardingQueuePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { status: authStatus } = useAdminAuth();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const [claimErrors, setClaimErrors] = useState<Record<string, string>>({});

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: queryKeys.onboardingQueue(statusFilter),
    queryFn: () => fetchOnboardingQueue({ status: statusFilter }),
    enabled: authStatus === 'authenticated',
  });

  const { data: stats, isPending: statsLoading } = useQuery({
    queryKey: ['onboarding-queue-stats'] as const,
    queryFn: fetchOnboardingQueueStats,
    staleTime: 60_000,
    enabled: authStatus === 'authenticated',
  });

  const latestItems = data?.items ?? [];
  const latestCursor = data?.nextCursor ?? null;
  const [appendedPages, setAppendedPages] = useState<OnboardingQueueItem[][]>([]);
  const [appendedCursors, setAppendedCursors] = useState<(string | null)[]>([]);

  // Reset appended pages when filter changes
  const handleFilterChange = useCallback((s: StatusFilter) => {
    setStatusFilter(s);
    setAppendedPages([]);
    setAppendedCursors([]);
    setLoadMoreError(null);
  }, []);

  // Build full item list: first-page items + appended
  const allItems = [...latestItems, ...appendedPages.flat()];
  const nextCursor =
    appendedCursors.length > 0 ? appendedCursors[appendedCursors.length - 1] : latestCursor;

  const claimMutation = useMutation({
    mutationFn: (tenantId: string) => claimOnboardingTenant(tenantId),
    onSuccess: (_data, tenantId) => {
      setClaimErrors((prev) => ({ ...prev, [tenantId]: '' }));
      void queryClient.invalidateQueries({ queryKey: queryKeys.onboardingQueue(statusFilter) });
      setAppendedPages([]);
      setAppendedCursors([]);
    },
    onError: (err, tenantId) => {
      setClaimErrors((prev) => ({
        ...prev,
        [tenantId]: err instanceof Error ? err.message : 'Claim failed',
      }));
    },
  });

  const handleLoadMore = useCallback(async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    setLoadMoreError(null);
    try {
      const res = await fetchOnboardingQueue({ status: statusFilter, cursor: nextCursor });
      setAppendedPages((prev) => [...prev, res.items]);
      setAppendedCursors((prev) => [...prev, res.nextCursor]);
    } catch (err) {
      setLoadMoreError(err instanceof Error ? err.message : 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, statusFilter]);

  if (isError) return <ErrorState onRetry={() => void refetch()} />;

  return (
    <Tabs
      activeIndex={0}
      onChange={(i) => {
        if (i === 1) void navigate({ to: '/onboarding/funnel' });
      }}
    >
      <Tab label="Queue">
        <div className="flex flex-col gap-4">
          {/* Header */}
          <div>
            <h1 className="text-heading-2 text-primary">Onboarding Stalls</h1>
            <p className="mt-1 text-body-sm text-muted">
              Tenants that have stalled during onboarding.
            </p>
          </div>

          {/* Stats chips */}
          <div className="flex flex-wrap gap-3">
            <Chip label="Needs CS" value={stats?.needsCs ?? null} loading={statsLoading} />
            <Chip
              label="Unclaimed Stuck"
              value={stats?.unclaimedStuck ?? null}
              loading={statsLoading}
            />
            <Chip label="Stale Claims" value={stats?.staleClaims ?? null} loading={statsLoading} />
            <Chip
              label="Go-Live Last 7d"
              value={stats?.goliveLastSevenDays ?? null}
              loading={statsLoading}
            />
            <Chip
              label="CS Interventions (7d)"
              value={stats?.csInterventionInflowSevenDays ?? null}
              loading={statsLoading}
            />
          </div>

          {/* Status filter */}
          <div role="tablist" className="flex flex-wrap gap-2">
            {(
              [
                'all',
                'unclaimed',
                'claimed_by_me',
                'claimed_by_other',
                'expired',
                'snoozed',
                'new_applications',
              ] as StatusFilter[]
            ).map((s) => (
              <Button
                key={s}
                size="sm"
                variant={statusFilter === s ? 'secondary' : 'ghost'}
                role="tab"
                aria-selected={statusFilter === s}
                onClick={() => handleFilterChange(s)}
              >
                {s === 'claimed_by_me'
                  ? 'Mine'
                  : s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ')}
              </Button>
            ))}
          </div>

          {/* Empty state */}
          {!isPending && !isError && allItems.length === 0 && (
            <EmptyState
              title="No stalled onboarding tenants"
              description="All onboarding tenants are on track."
            />
          )}

          {/* Table */}
          {(isPending || allItems.length > 0) && (
            <div className="overflow-x-auto">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Tenant</TableHeaderCell>
                    <TableHeaderCell>Setup Guide</TableHeaderCell>
                    <TableHeaderCell>Stall age</TableHeaderCell>
                    <TableHeaderCell>No progress for</TableHeaderCell>
                    <TableHeaderCell>Claim</TableHeaderCell>
                    <TableHeaderCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isPending
                    ? Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={i} data-testid="skeleton-row">
                          <TableCell
                            className="animate-pulse bg-surface-sunken h-6 rounded-sm"
                            colSpan={6}
                          />
                        </TableRow>
                      ))
                    : allItems.map((row) => (
                        <TableRow key={row.tenantId}>
                          <TableCell>
                            <span className="text-body-emphasis text-primary">
                              {row.tenantName}
                            </span>
                          </TableCell>
                          <TableCell className="tabular-nums whitespace-nowrap text-secondary">
                            {row.guideProgress ? (
                              <span title={`${row.guideProgress.percentage}% complete`}>
                                {row.guideProgress.completed} / {row.guideProgress.total}
                              </span>
                            ) : (
                              <span aria-label="Setup Guide progress unavailable">—</span>
                            )}
                          </TableCell>
                          <TableCell className="tabular-nums text-secondary">
                            {row.stallDay != null ? `${row.stallDay}d` : '—'}
                          </TableCell>
                          <TableCell>
                            {row.daysSinceActivity != null ? (
                              <span className="flex items-center gap-1.5 tabular-nums text-secondary">
                                {row.daysSinceActivity}d
                                {row.daysSinceActivity >= 7 && (
                                  <Badge variant="warning">Stuck</Badge>
                                )}
                              </span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={claimVariant(row.claimStatus)}>
                              {row.claimStatus.replace(/_/g, ' ')}
                            </Badge>
                            {row.claimExpiresAt && (
                              <p className={`text-caption ${claimExpiryClass(row.claimExpiresAt)}`}>
                                {formatClaimExpiry(row.claimExpiresAt)}
                              </p>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              <div className="flex gap-2">
                                {row.claimStatus === 'unclaimed' && (
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    disabled={
                                      claimMutation.isPending &&
                                      claimMutation.variables === row.tenantId
                                    }
                                    onClick={() => claimMutation.mutate(row.tenantId)}
                                  >
                                    {claimMutation.isPending &&
                                    claimMutation.variables === row.tenantId
                                      ? 'Claiming…'
                                      : 'Claim'}
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() =>
                                    void navigate({
                                      to: '/tenants/$tenantId',
                                      params: { tenantId: row.tenantId },
                                    })
                                  }
                                >
                                  Open
                                </Button>
                              </div>
                              {claimErrors[row.tenantId] && (
                                <p className="text-caption text-danger-text">
                                  {claimErrors[row.tenantId]}
                                </p>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Load more */}
          {!isPending && nextCursor && (
            <div className="flex flex-col items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                disabled={loadingMore}
                onClick={() => void handleLoadMore()}
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </Button>
              {loadMoreError && <p className="text-caption text-danger-text">{loadMoreError}</p>}
            </div>
          )}
        </div>
      </Tab>
      <Tab label="Funnel Dashboard">
        <div data-testid="funnel-tab-redirect-target" />
      </Tab>
    </Tabs>
  );
}
